import * as yup from 'yup';

import { isGreaterOrEqualThan } from '@/shared/utils/helpers';

export enum LeverageStrategy {
    PREDEFINED_WITH_PERCENT = 'PREDEFINED_WITH_PERCENT',
    MANUAL = 'MANUAL',
}

const stripCommas = (schema: yup.StringSchema) =>
    schema.transform((val) => (typeof val === 'string' ? val.replace(/,/g, '') : val));

const borrowModalSchema = yup.object({
    assetAmount: stripCommas(
        yup
            .string()
            .test('is-valid-number', 'Asset amount must be a valid number', (value) => {
                return value !== undefined && !isNaN(Number(value));
            })
            .test('is-non-zero', 'Asset amount must be higher than 0', (value) => {
                return value !== undefined && Number(value) !== 0;
            })
            .test(
                'is-valid-leverage-amount',
                'Leverage amount is higher than max available balance',
                function (value) {
                    const { maxBorrow } = this.parent;
                    if (value === undefined || value === null) return true;
                    return isGreaterOrEqualThan(maxBorrow, value);
                },
            )
            .required('Asset amount is required'),
    ),

    maxBorrow: stripCommas(yup.string().required('Max borrow is required')),

    selectedAssets: yup
        .array()
        .of(
            yup.object({
                name: yup.string().required('Name is required'),

                amount: stripCommas(
                    yup
                        .string()
                        .test(
                            'is-valid-amount',
                            'Insufficient funds in your wallet to complete this transaction. Please adjust the collateral amount or fund your wallet to proceed.',
                            function (value) {
                                const { maxBalance } = this.parent;
                                if (value === undefined || value === null) return true;
                                return isGreaterOrEqualThan(maxBalance, value);
                            },
                        )
                        .test('is-positive', 'Amount must be greater than 0', (value) => {
                            return value !== undefined && Number(value) > 0;
                        })
                        .required('Amount is required'),
                ),

                maxBalance: stripCommas(
                    yup.string().required('Max balance is required for validation'),
                ),

                amountInAssetToBorrow: stripCommas(
                    yup.string().required('Amount in asset to borrow is required'),
                ),

                address: yup
                    .string()
                    .matches(/^0x[a-fA-F0-9]{40}$/, 'Address must be a valid Ethereum address'),

                symbol: yup.string().required('Symbol is required'),

                decimals: yup.number().required('Decimals is required'),
            }),
        )
        .min(1, 'At least one asset must be selected'),

    strategy: yup.string().default(LeverageStrategy.MANUAL),
});

export type BorrowModalData = yup.InferType<typeof borrowModalSchema>;

export default borrowModalSchema;
